import React from 'react';
import { motion } from 'framer-motion';
import { Users, Target, Award, Zap, Heart, Globe, CheckCircle2 } from 'lucide-react';
import TrainersSection from '@/components/about/TrainersSection';

const founders = [
  {
    name: 'SURYA PRAKASH',
    role: 'Co-Founder & Chief Executive Officer (CEO)',
    initials: 'SP',
    bio: 'Entrepreneur and business leader driving the strategic vision, growth, and industry partnerships at XceedEdge. Passionate about making quality technology education accessible and helping students transform their careers through skill-based learning and mentorship.',
    highlights: [
      'Business Strategy & Growth',
      'Industry Partnerships',
      'Student Success Initiatives',
      'Career Development Programs',
    ],
    image: '',
  },
  {
    name: 'HARISHWAR REDDY',
    role: 'Co-Founder & Chief Technology Officer (CTO)',
    initials: 'HR',
    bio: 'Technology leader and cybersecurity professional with expertise in Security Operations, SIEM, Threat Detection, Infrastructure Security, and Learning Platform Development. Focused on building industry-relevant training programs and practical learning environments that help students become job-ready professionals.',
    highlights: [
      'Cyber Security & SOC Specialist',
      'SIEM & Security Operations Expert',
      'Learning Platform Architect',
      'Industry-Oriented Curriculum Designer',
    ],
    image: '',
  },
];

const easeOut = [0.22, 1, 0.36, 1];

const gridVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.18 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: easeOut, staggerChildren: 0.08, delayChildren: 0.2 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: easeOut } },
};

const values = [
  { icon: Target, title: 'Industry-Focused', desc: 'Every course is designed around real job roles and market demands.' },
  { icon: Zap, title: 'Hands-On Learning', desc: 'Learn by doing with live projects, labs, and real-world assignments.' },
  { icon: Award, title: 'Certified Excellence', desc: 'Industry-recognized certifications that employers trust.' },
  { icon: Heart, title: 'Student-First', desc: 'Dedicated mentorship, placement support, and lifetime access.' },
  { icon: Globe, title: 'Accessible', desc: 'Quality education available to every learner across India.' },
  { icon: Users, title: 'Community', desc: '2,000+ alumni network for networking and career opportunities.' },
];

export default function About() {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-primary/10 via-background to-accent/5">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-block px-4 py-1.5 bg-primary/10 text-primary text-sm font-semibold rounded-full mb-4">About XceedEdge</span>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-6">
              Empowering India's Next<br /><span className="text-primary">Tech Generation</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              XceedEdge is India's premier tech learning platform, built to bridge the gap between education and employment. 
              We offer job-ready courses in AI, Cybersecurity, Full Stack, and more taught by industry experts with real-world experience.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-card border-y">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { label: 'Students Enrolled', value: '2,000+' },
            { label: 'Placement Rate', value: '94%' },
            { label: 'Expert Instructors', value: '25+' },
            { label: 'Industry Partners', value: '10+' },
          ].map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
              <div className="text-3xl font-heading font-bold text-primary mb-1">{s.value}</div>
              <div className="text-sm text-muted-foreground">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Mission */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 grid md:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <h2 className="font-heading text-3xl font-bold mb-4">Our Mission</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We started XceedEdge with a simple belief: every motivated learner deserves access to world-class tech education, 
              regardless of their background or location.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Our curriculum is designed by practitioners not just academicians. Every course includes live projects, 
              mentorship, and placement assistance to ensure you don't just learn, you launch your career.
            </p>
          </motion.div>
        <motion.div
  initial={{ opacity: 0, x: 30 }}
  whileInView={{ opacity: 1, x: 0 }}
  viewport={{ once: true }}
  className="bg-gradient-to-br from-primary/20 to-accent/10 rounded-2xl p-8 text-center"
>
  {/* Brand Impact */}
  <div className="text-5xl font-heading font-black text-primary mb-3">
    2,000+
  </div>
  <p className="text-muted-foreground">
    Students trained across Full Stack, AI & Cybersecurity
  </p>

  {/* Divider */}
  <div className="my-4 h-px bg-border/40" />

  {/* Outcome */}
  <div className="text-3xl font-heading font-bold text-foreground">
    94%+ Placement Success Rate
  </div>
  <p className="text-muted-foreground mt-1 text-sm">
    Supported by hands-on training, real-world projects & mentor guidance
  </p>

  {/* Secondary Proof */}
  <div className="mt-6 text-xl font-semibold text-primary">
    ₹3.5L → ₹8L+ Avg Salary Growth
  </div>
</motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold mb-3">What We Stand For</h2>
            <p className="text-muted-foreground">The principles that guide everything we do</p>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
            {values.map((v, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="bg-card rounded-xl p-6 border hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                  <v.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading font-semibold text-lg mb-2">{v.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 bg-primary/10 text-primary text-sm font-semibold rounded-full mb-4">Leadership Team</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">Meet The Founders</h2>
            <p className="text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              The visionaries behind XceedEdge Technologies, committed to building India's next generation of
              technology professionals bridging the gap between education and industry through practical,
              job-oriented learning.
            </p>
          </div>

          <motion.div
            variants={gridVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            className="grid md:grid-cols-2 gap-8"
          >
            {founders.map((f, i) => (
              <motion.article
                key={i}
                variants={cardVariants}
                whileHover={{ y: -8, transition: { type: 'spring', stiffness: 300, damping: 22 } }}
                className="group relative flex flex-col overflow-hidden rounded-2xl border bg-card shadow-sm transition-[box-shadow,border-color] duration-500 hover:border-primary/30 hover:shadow-2xl hover:shadow-primary/10"
              >
                {/* Gradient banner */}
                <div className="relative h-28 overflow-hidden bg-gradient-to-br from-primary/20 via-primary/10 to-accent/20">
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/30 to-accent/30 opacity-0 transition-opacity duration-700 group-hover:opacity-100" />
                  <div className="absolute -right-8 -top-10 h-28 w-28 rounded-full bg-white/10 blur-2xl" />
                  <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
                </div>

                <div className="-mt-14 flex flex-grow flex-col px-8 pb-8">
                  {/* Avatar */}
                  <motion.div variants={itemVariants} className="relative mx-auto">
                    <div className="absolute -inset-1.5 rounded-full bg-gradient-to-br from-primary to-accent opacity-0 blur-lg transition-opacity duration-500 group-hover:opacity-70" />
                    {f.image ? (
                      <img
                        src={f.image}
                        alt={f.name}
                        className="relative h-28 w-28 rounded-full object-cover ring-4 ring-card transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="relative flex h-28 w-28 items-center justify-center rounded-full bg-gradient-to-br from-primary to-accent ring-4 ring-card transition-transform duration-500 group-hover:scale-105">
                        <span className="font-heading text-3xl font-bold tracking-wider text-white">{f.initials}</span>
                      </div>
                    )}
                  </motion.div>

                  {/* Identity */}
                  <motion.div variants={itemVariants} className="mt-5 text-center">
                    <h3 className="font-heading text-xl font-bold tracking-wide leading-tight">{f.name}</h3>
                    <span className="mt-2.5 inline-block rounded-full bg-primary/10 px-3.5 py-1.5 text-xs font-semibold text-primary">
                      {f.role}
                    </span>
                  </motion.div>

                  {/* Bio */}
                  <motion.p
                    variants={itemVariants}
                    className="mt-5 flex-grow text-center text-sm leading-relaxed text-muted-foreground"
                  >
                    {f.bio}
                  </motion.p>

                  {/* Highlights */}
                  <motion.div variants={itemVariants} className="mt-6 border-t border-border/50 pt-6">
                    <div className="grid gap-2.5 sm:grid-cols-2">
                      {f.highlights.map((h, j) => (
                        <div
                          key={j}
                          className="flex items-start gap-2 rounded-lg bg-muted/50 px-3 py-2.5 transition-colors duration-500 group-hover:bg-primary/5"
                        >
                          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                          <span className="text-xs font-medium leading-snug text-foreground/80">{h}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                </div>
              </motion.article>
            ))}
          </motion.div>

          {/* Section intro */}
          <p className="text-center text-muted-foreground leading-relaxed max-w-3xl mx-auto mt-12">
            At XceedEdge Technologies, our founders bring together expertise in technology, cybersecurity,
            education, and business leadership. Their shared mission is to empower students with practical skills,
            real-world exposure, and career-focused training that aligns with industry demands.
          </p>
        </div>
      </section>

      {/* Trainers — managed from the admin panel (Trainers page) */}
      <TrainersSection />

    </div>
  );
}